/**
 * @module Components.StripeForm
 *
 */
import React, { useState, FormEvent, useEffect, useRef, useMemo } from "react";
import { injectStripe } from "react-stripe-elements";
import { MessageDescriptor } from "react-intl";
import { cls } from "app/utils";
import { useActions, useError, usePending } from "app/utils/hooks";
import { Token, ElementChangeResponse } from "app/utils/stripe";
import { Spinner } from "app/components/Loading";
import CardName from "../ProCheckout/_components/CardName";
import CardNumber from "../ProCheckout/_components/CardNumber";
import CardExpiry from "../ProCheckout/_components/CardExpiry";
import CardCVC from "../ProCheckout/_components/CardCVC";
import PostalCode from "../ProCheckout/_components/PostalCode";
import AddressLineOne from "../ProCheckout/_components/AddressLineOne";
import AddressLinetwo from "../ProCheckout/_components/AddressLineTwo";
import City from "../ProCheckout/_components/City";
import Country from "../ProCheckout/_components/Country";
import UsState from "../usState";
import styles from "../ProCheckout/styles.module.scss";
import { useTranslation } from "react-i18next";
import FormItem from "app/components/inputs/FormItem";
import ButtonVariant from "app/components/Button/ButtonVariant";
import PaymentMethodSelector from "app/components/StripeForm/PaymentMethodSelector";
import { purchaseSubscription, updateSubscription } from "app/actions/stripe";
import { fetchSelf } from "app/actions/user";
import { useSavedCard } from "app/hooks/useSavedCard";
import { useSelector } from "react-redux";
import { RootState } from "typedefs";
import {
  useTaxCalculation,
  TaxCalculationResult,
} from "app/hooks/useTaxCalculation";
import { Currency, formatMoney, formatPrice } from "app/utils/currency";
import { AnalyzePlanCode, getDiscountedAmount } from "app/utils/planPricing";

export type PropsDef = {
  disabled?: boolean;
  cardName?: string;
  cardNumber?: string;
  cardExpiry?: string;
  cardSecurityCode?: string;
  country?: string;
  usState?: string;
  city?: string;
  addressLineOne?: string;
  addressLineTwo?: string;
  postalCode?: string;
  submitTitle?: string;
  // The second argument says what the submission was: a re-price of the
  // existing subscription, or an ordinary purchase. The page cannot derive it
  // afterwards — the refreshed account has re-price targets of its own.
  onToken?: (token?: Token, reprice?: boolean) => void;
  onCancel?: () => void;
  onTaxCalculated?: (taxData: TaxCalculationResult | null) => void;
  stripe?: { [key: string]: any };
  planCode?: AnalyzePlanCode;
  couponCode?: string;
  currency?: Currency;
  // Direct purchase (/purchase/analyze): charge immediately (no start_trial
  // flag) and hand success back to the page instead of the marketing redirect.
  purchase?: boolean;
  // Re-price the user's own Stripe subscription onto planCode (PUT
  // subscriptions/update) instead of buying a new one; the saved card pays.
  reprice?: boolean;
  // The trial paywall's buttons, from its GrowthBook flag. Each falls back to
  // today's copy; showFreePlanButton false hides the free plan button.
  ctaText?: string;
  showFreePlanButton?: boolean;
  freePlanText?: string;
};

type ErrorModel = {
  cardNameError?: MessageDescriptor | null;
  cardNumberError?: string | null;
  cardExpiryError?: string | null;
  cardSecurityCodeError?: string | null;
  countryError?: string;
  usStateError?: string;
  cityError?: string;
  addressLineOneError?: string;
  addressLineTwoError?: string;
  postalCodeError?: string | null;
  formError?: string | null;
};

type State = ErrorModel & {
  cardName: ElementChangeResponse | null;
  cardNumber: ElementChangeResponse | null;
  cardExpiry: ElementChangeResponse | null;
  cardSecurityCode: ElementChangeResponse | null;
  country?: ElementChangeResponse | null;
  usState?: ElementChangeResponse | null;
  city?: ElementChangeResponse | null;
  addressLineOne?: ElementChangeResponse | null;
  addressLineTwo?: ElementChangeResponse | null;
  postalCode: ElementChangeResponse | null;
};

const initError: ErrorModel = {
  cardNameError: null,
  cardNumberError: null,
  cardExpiryError: null,
  cardSecurityCodeError: null,
  countryError: null,
  usStateError: null,
  cityError: null,
  addressLineOneError: null,
  addressLineTwoError: null,
  postalCodeError: null,
  formError: null,
};

const initState: State = {
  cardName: null,
  cardNumber: null,
  cardExpiry: null,
  country: null,
  usState: null,
  city: null,
  cardSecurityCode: null,
  addressLineOne: null,
  addressLineTwo: null,
  postalCode: null,
};

const TRIAL_SUCCESS_URL = "https://coachnow.com/analyze-trial-success";

// Stripe Product is implicit server-side for POST /api/v1/stripe/checkout —
// the server selects it from plan_code. We still pass a productKind because
// the shared PurchaseParams type requires it; backend ignores the value.
const PRODUCT_KIND = "Analyze";

function AnalyzeCheckoutForm(props: PropsDef) {
  const { t } = useTranslation();
  const [isReady, setIsReady] = useState(false);
  // A logged-in user who arrived via a trial/purchase link may already have a
  // card on file — offer it as a payment option instead of the billing form.
  const { paymentInfo, hasSavedCard, useNewCard, setUseNewCard, paymentInfoLoaded } = useSavedCard();
  const [pending, startPending, stopPending] = usePending();
  const [errors, addErrors, clearErrors] = useError(initError);
  const [state, setState] = useState(() =>
    mergePropsWithState(props, initState),
  );
  const { taxData, error: taxError, calculateTax } = useTaxCalculation();
  const taxCalculationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMounted = useRef(true);

  const actions = useActions({
    purchaseSubscription,
    updateSubscription,
    fetchSelf,
  });

  // A successful submit navigates away (full-page redirect / success screen),
  // which unmounts this form; guard the stopPending that runs in the finally.
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Base price + discount come from the resolved offer the PAGE fetched (shared
  // state.promoOffer). Guard against a resolution left over from a different code.
  const resolution = useSelector((state: RootState) => state.promoOffer);

  const formClass = cls(styles.stripeCardForm, isReady ? "" : "hidden");
  const disabled = props.disabled || pending.status;
  const currency = props.currency || "usd";
  const couponCode = props.couponCode;
  // Match the plan too, not just the code: on a month↔year toggle the code stays
  // but planCode changes, so a code-only check would feed the previous term's
  // baseAmount into the tax calc while resolving.
  const isCurrentOffer =
    (resolution.code || "").toUpperCase() === (couponCode || "").toUpperCase() &&
    resolution.planCode === props.planCode &&
    resolution.currency === currency;
  const offer = isCurrentOffer ? resolution.offer : null;
  const discount = offer?.discount;
  const planAmount = (isCurrentOffer ? resolution.baseAmount : 0) || 0;
  // Backend-resolved (region-aware, carried even by a `none` offer); 7 covers
  // the transient not-yet-fresh render (the trial line is hidden while loading).
  const trialDays = (isCurrentOffer ? resolution.trialDays : null) ?? 7;
  // Not fresh while resolving OR mid term-switch — hide the price line rather
  // than flash the previous term's amount.
  const loading =
    resolution.pending ||
    (resolution.code || "").toUpperCase() !== (couponCode || "").toUpperCase() ||
    resolution.planCode !== props.planCode ||
    resolution.currency !== currency;
  // The preview failed (the page offers a retry): there is no price to show
  const offerError = isCurrentOffer && resolution.error;
  const taxableAmount = useMemo(
    () => getDiscountedAmount(planAmount, discount),
    [planAmount, discount],
  );

  // Price message shown above the button, mirroring the pro trial wording:
  // the real (discounted) price plus tax once the address is known.
  const priceMessage = useMemo(() => {
    // Resolving (or mid term-switch): hide the price line rather than flash the
    // previous term's amount / the 7-day default.
    if (loading || offerError) {
      return null;
    }
    const isYearly = props?.planCode === "analyze+yearly";
    const interval = t(isYearly ? "year" : "month");
    const billingText = t(isYearly ? "billed annually" : "billed monthly");

    // A temporary discount only covers the first invoice — say so, like the
    // pro checkout does.
    const usesFirst = taxableAmount < planAmount && discount?.duration !== "forever";

    if (taxData?.total && taxData?.taxAmount) {
      const values = {
        trialDays,
        totalWithTax: formatMoney(taxData.total, currency),
        taxAmount: formatMoney(taxData.taxAmount, currency),
        interval,
        billingText,
      };
      if (props.purchase) {
        return usesFirst
          ? t("{{totalWithTax}} per first {{interval}} (incl. {{taxAmount}} tax) / {{billingText}}", values)
          : t("{{totalWithTax}} per {{interval}} (incl. {{taxAmount}} tax) / {{billingText}}", values);
      }
      return usesFirst
        ? t("{{trialDays}} days free, then {{totalWithTax}} per first {{interval}} (incl. {{taxAmount}} tax) / {{billingText}}", values)
        : t("{{trialDays}} days free, then {{totalWithTax}} per {{interval}} (incl. {{taxAmount}} tax) / {{billingText}}", values);
    }

    const values = {
      trialDays,
      basePrice: formatPrice(taxableAmount, currency),
      interval,
      billingText,
    };
    if (props.purchase) {
      return usesFirst
        ? t("{{basePrice}} per first {{interval}} / {{billingText}}", values)
        : t("{{basePrice}} per {{interval}} / {{billingText}}", values);
    }
    return usesFirst
      ? t("{{trialDays}} days free, then {{basePrice}} per first {{interval}} / {{billingText}}", values)
      : t("{{trialDays}} days free, then {{basePrice}} per {{interval}} / {{billingText}}", values);
  }, [loading, offerError, taxData, props?.planCode, taxableAmount, planAmount, discount, currency, trialDays, props.purchase, t]);

  useEffect(() => {
    if (props.onTaxCalculated) {
      props.onTaxCalculated(taxData);
    }
  }, [taxData, props.onTaxCalculated]);

  // The four triggers below (address effects and blur handlers) share one
  // guarded call; overrides let blur handlers use the just-entered value
  // before it lands in state. Always taxes the discounted amount.
  const runTaxCalc = (overrides: { postalCode?: string; city?: string } = {}) => {
    const country = state.country?.value as string | undefined;
    const postalCode = overrides.postalCode ?? (state.postalCode?.value as string | undefined);
    const usState = state.usState?.value as string | undefined;
    const city = overrides.city ?? (state.city?.value as string | undefined);

    if (planAmount && props.planCode && country && postalCode) {
      calculateTax({
        amount: taxableAmount,
        priceId: props.planCode,
        country,
        state: usState,
        postalCode,
        city,
      });
    }
  };

  const cancelPendingTaxCalc = () => {
    if (taxCalculationTimerRef.current) {
      clearTimeout(taxCalculationTimerRef.current);
      taxCalculationTimerRef.current = null;
    }
  };

  // Immediate recalculation on country, state, plan or discount change
  useEffect(() => {
    runTaxCalc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.country?.value, state.usState?.value, taxableAmount, props.planCode]);

  // Debounced recalculation on postal code or city change
  useEffect(() => {
    cancelPendingTaxCalc();
    taxCalculationTimerRef.current = setTimeout(runTaxCalc, 1500);

    return cancelPendingTaxCalc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.postalCode?.value, state.city?.value]);

  const onStateChange = (name: string, el: ElementChangeResponse) => {
    clearErrors();
    setState({ ...state, [name]: el });
  };

  const handlePostalCodeBlur = (el: ElementChangeResponse) => {
    onStateChange("postalCode", el);
    cancelPendingTaxCalc();
    runTaxCalc({ postalCode: el.value as string | undefined });
  };

  const handleCityBlur = (el: ElementChangeResponse) => {
    onStateChange("city", el);
    cancelPendingTaxCalc();
    runTaxCalc({ city: el.value as string | undefined });
  };

  const handlePurchaseFreeTrial = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!props.stripe) {
      addErrors({ formError: t("Something went wrong. Please try again later") });
      return;
    }

    if (planAmount && props.planCode) {
      if (!taxData || taxError) {
        alert(t("Please provide a valid billing address"));
        return;
      }
    }

    const { cardNumber, cardExpiry, cardSecurityCode } = state;

    const cardName =
      state.cardName && state.cardName.value
        ? (state.cardName.value as string).trim()
        : null;
    const country =
      state.country && state.country.value
        ? (state.country.value as string)
        : null;
    const usState =
      state.usState && state.usState.value
        ? (state.usState.value as string)
        : null;
    const city =
      state.city && state.city.value
        ? (state.city.value as string).trimLeft()
        : null;
    const addressLineOne =
      state.addressLineOne && state.addressLineOne.value
        ? (state.addressLineOne.value as string).trimLeft()
        : null;
    const addressLineTwo =
      state.addressLineTwo && state.addressLineTwo.value
        ? (state.addressLineTwo.value as string).trimLeft()
        : null;
    const postalCode =
      state.postalCode && state.postalCode.value
        ? (state.postalCode.value as string).trim()
        : null;

    const errors = validate(
      cardName,
      cardNumber,
      cardExpiry,
      cardSecurityCode,
      country,
      usState,
      city,
      addressLineOne,
      postalCode,
      t,
    );

    if (Object.keys(errors).length) {
      addErrors(errors);
      return;
    }

    startPending();

    try {
      const token = await props.stripe.createToken({
        name: cardName,
        address_country: country,
        address_city: city,
        address_state: usState || "",
        address_line1: addressLineOne,
        address_line2: addressLineTwo || "",
        address_zip: postalCode,
      });

      if (token.error) {
        let error: ErrorModel = { formError: token.error.message };
        switch (token.error.code) {
          case "incomplete_zip":
            error = { postalCodeError: token.error.message };
            break;
          case "card_declined":
            error = { cardNumberError: token.error.message };
            break;
          case "expired_card":
            error = { cardExpiryError: token.error.message };
            break;
          default:
            error = { formError: token.error.message };
        }
        addErrors(error);
        return;
      }

      const respToken = token?.token?.id;
      if (!respToken) {
        addErrors({ formError: t("Something went wrong. Please try again later") });
        return;
      }

      const purchased = await purchasePlan(respToken);
      if (!purchased) {
        return;
      }

      if (props.onToken) {
        props.onToken(token.token, props.reprice);
      }

      if (!props.purchase) {
        // Marketing success page, same pattern as the academy/pro trials.
        // A direct purchase stays in-app: the page shows its success screen.
        window.location.href = TRIAL_SUCCESS_URL;
      }
    } catch (err) {
      addErrors({
        formError:
          err instanceof Error ? err.message : t("Something went wrong. Please try again later"),
      });
    } finally {
      if (isMounted.current) {
        stopPending();
      }
    }
  };

  // Returns whether the purchase succeeded; a failure surfaces the error to
  // the user and must not fall through to the success redirect. `savedCard`
  // relaxes the token guard so the handler can pass the saved card's id (or ""
  // when the backend exposes none) — not a `tok_…` value, so the checkout skips
  // the card update and subscribes on the customer's existing default payment method.
  const purchasePlan = async (
    token: string,
    savedCard = false,
  ): Promise<boolean> => {
    if ((!token && !savedCard) || !props.planCode) {
      addErrors({ formError: t("Something went wrong. Please try again later") });
      return false;
    }

    // A re-price moves the existing subscription onto this plan, the saved
    // card paying for the new period; nothing is bought anew.
    if (props.reprice) {
      const updated: any = await actions.updateSubscription(props.planCode, couponCode || null);
      if (updated.type === "@stripe.post.success") {
        // The plan just changed under the loaded account: refresh it (forced,
        // fetchSelf is a no-op once loaded) so the app's plan gates see the
        // new plan without a reload.
        try {
          await actions.fetchSelf(true);
        } catch (error) {
          console.error("Error refreshing account after the plan change:", error);
        }
        return true;
      }
      addErrors({
        formError:
          updated?.payload?.defaultMessage ||
          t("Something went wrong. Please try again later"),
      });
      return false;
    }

    const resp: any = await actions.purchaseSubscription({
      token,
      couponCode,
      productKind: PRODUCT_KIND,
      planCode: props.planCode,
      currency,
      // Analyze has no default trial trigger server-side (unlike academy
      // plans or the pro page's built-in code) — request the trial explicitly.
      // A direct purchase omits the flag, so the charge happens immediately.
      ...(props.purchase ? {} : { startTrial: true }),
    });

    if (resp.type === "@stripe.post.success") {
      // The purchase changes the plan under the loaded account (a cancelled
      // store plan, say): refresh it so the app's gates see the new one.
      try {
        await actions.fetchSelf(true);
      } catch (error) {
        console.error("Error refreshing account after purchase:", error);
      }
      return true;
    }

    // The error action carries a MessageDescriptor (see stripe.ts ERROR_MAP).
    addErrors({
      formError:
        resp?.payload?.defaultMessage || t("Something went wrong. Please try again later"),
    });
    return false;
  };

  const handleKeyboard = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.which === 13) {
      e.preventDefault();
      handlePurchaseFreeTrial(e);
    }
  };

  const handleTrialClicked = () => {
    (window as any).location = "/";
  };

  // Purchase/trial on the saved card — no token created, no billing address
  // entered. The backend reuses the customer's payment method and computes tax
  // from their saved address (automatic_tax), same as the /upgrade flow.
  const handlePurchaseWithSavedCard = async () => {
    if (!paymentInfo?.cardLast4) {
      return;
    }

    startPending();
    try {
      const purchased = await purchasePlan(paymentInfo.cardId ?? "", true);
      if (!purchased) {
        return;
      }

      if (props.purchase) {
        // Hand success back to the page (the token arg is unused by onDone),
        // which swaps to its in-app success screen.
        props.onToken?.(undefined, props.reprice);
      } else {
        window.location.href = TRIAL_SUCCESS_URL;
      }
    } finally {
      if (isMounted.current) {
        stopPending();
      }
    }
  };

  // Wait for the saved-card lookup before choosing the form vs. saved-card view.
  if (!paymentInfoLoaded) {
    return <Spinner />;
  }

  // User already has a card on file: skip the billing form, let them pay on the
  // saved card, with the option to enter a different one.
  // A re-price pays with the card on file: without one there is nothing to
  // charge, and a card is added from the billing settings, not here.
  if (props.reprice && !hasSavedCard) {
    return (
      <div className={styles.rcStripeForm}>
        <p className={styles.small_description}>
          {t("To switch plans, add a card in your billing settings first.")}
        </p>
      </div>
    );
  }

  if (hasSavedCard && !useNewCard) {
    return (
      <div className={cls(styles.rcStripeForm, disabled ? "disable" : "")}>
        <PaymentMethodSelector
          paymentInfo={paymentInfo}
          useSavedCard={!useNewCard}
          onChange={() => setUseNewCard(!useNewCard)}
          disabled={disabled || props.reprice}
        />
        <FormItem error={errors.formError}>
          <div className={styles.buttons_wrapper}>
            <div className={styles.price_description_wrapper}>
              <p className={styles.small_description}>{priceMessage}</p>
            </div>
            <ButtonVariant
              title={props.reprice ? t("Purchase") : props.purchase ? t("Subscribe") : props.ctaText ?? t("Start Free Trial")}
              buttonType="primary"
              onClickFunc={handlePurchaseWithSavedCard}
              disabled={pending?.status || (props.reprice && (loading || offerError))}
            />
            {!props.reprice && props.showFreePlanButton !== false && (
              <ButtonVariant
                title={props.freePlanText ?? t("Continue with Free Plan")}
                buttonType="secondary-outline"
                onClickFunc={handleTrialClicked}
                disabled={pending?.status}
              />
            )}
          </div>
        </FormItem>
      </div>
    );
  }

  return (
    <div className={cls(styles.rcStripeForm, disabled ? "disable" : "")}>
      {!isReady && <Spinner />}

      {hasSavedCard && (
        <PaymentMethodSelector
          paymentInfo={paymentInfo}
          useSavedCard={!useNewCard}
          onChange={() => setUseNewCard(!useNewCard)}
          disabled={disabled}
        />
      )}

      <form
        className={formClass}
        onKeyDown={handleKeyboard}
        onSubmit={handlePurchaseFreeTrial}
        target="_self"
      >
        <CardName
          onChange={(el: ElementChangeResponse) =>
            onStateChange("cardName", el)
          }
          value={props.cardName}
          error={errors.cardNameError}
          disabled={disabled}
        />
        <CardNumber
          onReady={() => setIsReady(true)}
          onChange={(el: ElementChangeResponse) =>
            onStateChange("cardNumber", el)
          }
          value={props.cardNumber}
          error={errors.cardNumberError}
          disabled={disabled}
        />
        <div className={styles.card_details_wrapper}>
          <CardExpiry
            onChange={(el: ElementChangeResponse) =>
              onStateChange("cardExpiry", el)
            }
            value={props.cardExpiry}
            error={errors.cardExpiryError}
            disabled={disabled}
          />
          <CardCVC
            onChange={(el: ElementChangeResponse) =>
              onStateChange("cardSecurityCode", el)
            }
            value={props.cardSecurityCode}
            error={errors.cardSecurityCodeError}
            disabled={disabled}
          />
        </div>
        <Country
          onChange={(el: ElementChangeResponse) => onStateChange("country", el)}
          value={props.country}
          error={errors.countryError}
          disabled={disabled}
        />
        {state.country &&
          state.country.value &&
          state.country.value === "US" && (
            <UsState
              onChange={(el: ElementChangeResponse) =>
                onStateChange("usState", el)
              }
              value={props.usState}
              error={errors.usStateError}
              disabled={disabled}
            />
          )}
        <PostalCode
          onChange={(el: ElementChangeResponse) =>
            onStateChange("postalCode", el)
          }
          onBlur={handlePostalCodeBlur}
          value={props.postalCode}
          error={errors.postalCodeError}
          disabled={disabled}
        />
        <City
          onChange={(el: ElementChangeResponse) => onStateChange("city", el)}
          onBlur={handleCityBlur}
          value={props.city}
          error={errors.cityError}
          disabled={disabled}
        />
        <AddressLineOne
          onChange={(el: ElementChangeResponse) =>
            onStateChange("addressLineOne", el)
          }
          value={props.addressLineOne}
          error={errors.addressLineOneError}
          disabled={disabled}
        />
        <AddressLinetwo
          onChange={(el: ElementChangeResponse) =>
            onStateChange("addressLineTwo", el)
          }
          value={props.addressLineTwo}
          disabled={disabled}
        />
        <FormItem error={errors.formError}>
          <div className={styles.buttons_wrapper}>
            <div className={styles.price_description_wrapper}>
              <p className={styles.small_description}>{priceMessage}</p>
            </div>
            <ButtonVariant
              title={props.purchase ? t("Subscribe") : props.ctaText ?? t("Start Free Trial")}
              buttonType="primary"
              onClickFunc={handlePurchaseFreeTrial}
              disabled={pending?.status}
            />
            {props.showFreePlanButton !== false && (
              <ButtonVariant
                title={props.freePlanText ?? t("Continue with Free Plan")}
                buttonType="secondary-outline"
                onClickFunc={handleTrialClicked}
                disabled={pending?.status}
              />
            )}
          </div>
        </FormItem>
      </form>
    </div>
  );
}

export default injectStripe(AnalyzeCheckoutForm);

const mergePropsWithState = (props: PropsDef, state: State): State => {
  return Object.assign({}, state, {
    cardName: props.cardName,
    cardNumber: props.cardNumber,
    cardExpiry: props.cardExpiry,
    cardSecurityCode: props.cardSecurityCode,
    country: props.country,
    usState: props.usState,
    city: props.city,
    addressLineOne: props.addressLineOne,
    addressLineTwo: props.addressLineTwo,
    postalCode: props.postalCode,
  });
};

const validate = (
  cardName: string | null,
  cardNumber: ElementChangeResponse | null,
  cardExpiry: ElementChangeResponse | null,
  cardSecurityCode: ElementChangeResponse | null,
  country: string | null,
  usState: string | null,
  city: string | null,
  addressLineOne: string | null,
  postalCode: string | null,
  t: any,
) => {
  const errors: ErrorModel = {};

  if (!(cardName && cardName.length)) {
    errors.cardNameError = {
      id: "error.required.card-name",
      defaultMessage: t(
        "Please enter the name that appears on your credit card.",
      ),
    };
  }

  if (!cardNumber) {
    errors.cardNumberError = t("Please enter your credit card number.");
  }

  if (!cardExpiry) {
    errors.cardExpiryError = t("Please enter your card's expiration date.");
  }

  if (!cardSecurityCode) {
    errors.cardSecurityCodeError = t(
      "Please enter the security code that appears on your card.",
    );
  }

  if (!country) {
    errors.countryError = t("Please select a country.");
  }

  if (country && country === "US" && !usState) {
    errors.usStateError = t("Please select state.");
  }

  if (!city) {
    errors.cityError = t("Please enter a valid city.");
  }

  if (!addressLineOne) {
    errors.addressLineOneError = t("Please enter an address.");
  }

  if (!postalCode) {
    errors.postalCodeError = t("A postal code is required.");
  }
  return errors;
};
